import React from 'react';

import { ChangeHandler, NameValueList } from 'src/presentation/components/pages/integrations/types';
import CompactSection from 'src/presentation/components/pages/integrations/editorComponents/CompactSection';
import { InputType } from 'src/presentation/components/forms/InputField';
import NameValueListEditor from 'src/presentation/components/pages/integrations/editorComponents/NameValueListEditor';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  MaskedField,
  WrappedField,
} from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';

function renderCustomAdapterConfiguration(
  items: NameValueList,
  onAttrChange: (attr: string) => ChangeHandler,
  onHeaderListChange: (newList: NameValueList) => void,
  maskedFields: string[],
  maskedHeaders: string[]
): JSX.Element {
  return (
    <>
      <CompactSection
        description={`The Gladly Lookup API allows your organization to provide data services to power the 
                      agent's experience with a complete view of your customer's information, transactions, 
                      and activity. You can provide a web service that implements the Lookup API and Gladly 
                      will call it when that data is needed. If you have any questions, please take a look at 
                      the Customer Lookup Guide`}
      >
        <Row>
          <WrappedField
            attr="name"
            label="Name"
            placeholder="App Name"
            description="This is the name that will be displayed throughout Gladly"
            data-aid="custom-integration-name"
            autoFocus={true}
            onChange={onAttrChange('name')}
          />
        </Row>
        <Row>
          <WrappedField
            attr="connectionSettings.baseUrl"
            label="URL"
            description="This is the URL of your Lookup Adapter"
            placeholder="e.g. https://customer.company.com"
            data-aid="custom-integration-url"
            onChange={onAttrChange('connectionSettings.baseUrl')}
          />
        </Row>
      </CompactSection>
      <CompactSection
        header="Basic Authentication"
        description={`Passwords and keys used to connect to other accounts are treated as secrets in Gladly.
                      If you need to change an existing secret, use the clear button in the field to remove the current
                      secret and enter a new one`}
      >
        <Row>
          <MaskedField
            attr="secrets.credentials.username"
            label="Username"
            showClearButton={maskedFields.includes('credentials.username')}
            isMasked={maskedFields.includes('credentials.username')}
            data-aid="custom-integration-username"
            onChange={onAttrChange('secrets.credentials.username')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.credentials.password"
            label="Password"
            type={InputType.password}
            showClearButton={maskedFields.includes('credentials.password')}
            isMasked={maskedFields.includes('credentials.password')}
            data-aid="custom-integration-password"
            onChange={onAttrChange('secrets.credentials.password')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.credentials.realm"
            label="Realm (optional)"
            showClearButton={maskedFields.includes('credentials.realm')}
            isMasked={maskedFields.includes('credentials.realm')}
            data-aid="custom-integration-realm"
            onChange={onAttrChange('secrets.credentials.realm')}
          />
        </Row>
      </CompactSection>
      <CompactSection header="Request Signature" headerMargin="small">
        <Row>
          <MaskedField
            attr="secrets.signingKey"
            label="Signing Key"
            description={`This cryptographic key will be used to sign each request that Gladly makes to your 
                          lookup service. Please refer to the Customer Lookup Guide for more information`}
            showClearButton={maskedFields.includes('signingKey')}
            isMasked={maskedFields.includes('signingKey')}
            data-aid="custom-integration-signing-key"
            onChange={onAttrChange('secrets.signingKey')}
          />
        </Row>
      </CompactSection>
      <CompactSection
        header="HTTP Headers"
        description={`Custom HTTP headers (e.g. X-API-Key) to include in every request to your lookup service`}
      >
        <Row>
          <NameValueListEditor
            addButtonLabel="Add a header"
            dataAid="custom-header-editor"
            items={items}
            maxItemCount={10}
            maskedItemNames={maskedHeaders}
            nameColumnLabel="Header"
            valueColumnLabel="Value"
            onChange={onHeaderListChange}
          />
        </Row>
      </CompactSection>
    </>
  );
}

export default renderCustomAdapterConfiguration;
